
import {
  defineComponent,
  defineAsyncComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeMount,
  provide,
} from "vue";
import { fabric } from "fabric";
import axios from "axios";
import { store } from "@/store";
import { ActionTypes } from "@/store/actions";

import { canvasWrap } from "@/canvas-api/Canvas";

import NavBar from "@/components/NavBar/NavBar.vue";
import SideToolBar from "@/components/SideToolBar/SideToolBar.vue";
import TopToolBar from "@/components/TopToolBar/TopToolBar.vue";
import Canvas from "@/components/Canvas/Canvas.vue";
import BottomToolBar from "@/components/BottomToolBar.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import OnBoarding from "@/components/Onboarding.vue";
import MobileHelpers from "@/components/MobileHelpers.vue";
import ModalManager from "@/components/Modals/ModalManager.vue";

const ColorPickr = defineAsyncComponent(() =>
  import("@/components/ColorPickr.vue" /* webpackChunkName: "colorPickr" */)
);

import { getEditorUrlParam, getUrlProperties } from "@/composables/useParseURL";

import { ColorPickrState, ClientDimensions, UrlProperties } from "./types";

export default defineComponent({
  name: "App",
  components: {
    NavBar,
    SideToolBar,
    ColorPickr,
    TopToolBar,
    Canvas,
    BottomToolBar,
    MobileHelpers,
    OnBoarding,
    LoadingScreen,
    ModalManager,
  },
  setup() {
    fabric.Object.NUM_FRACTION_DIGITS = 4;

    const urlProperties = ref<UrlProperties>(getUrlProperties());
    provide("urlProperties", urlProperties);

    onBeforeMount(async () => {
      store.dispatch(ActionTypes.SET_MOUNTING_LABEL, true);
      store.dispatch(ActionTypes.FETCH_SHOW_ONBOARDING);

      const pathName = window.location.pathname.substring(1);

      if (pathName.includes("create")) {
        //This is for admin users to create templates
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/editor/admin/ajax.php?mode=check_admin`
          );
          if (response.data.valid) {
            //If loading in a already built template

            if (urlProperties.value.isTemplate) {
              urlProperties.value.isCreatingTemplate = true;

              store.dispatch(ActionTypes.FETCH_TEMPLATE_AND_LABEL, {
                urlProperties: urlProperties.value,
                templateId: getEditorUrlParam("template_id"),
              });
            } else {
              urlProperties.value = {
                corners: "",
                isArtwork: false,
                isCustom: false,
                isCreatingTemplate: true,
                isTemplate: false,
                labelId: "SL102",
                labelType: "printed",
                quickLink: "",
              };

              store.dispatch(ActionTypes.FETCH_EDITOR_PARAMS, {
                pathName: "label-design/printed/SL102",
                urlProperties: urlProperties.value,
              });
            }
          }
        } catch (e) {
          console.log(e);
        }
      } else if (urlProperties.value.isArtwork) {
        store.dispatch(ActionTypes.FETCH_USER_ARTWORK, {
          pathName,
          urlProperties: urlProperties.value,
          hash: getEditorUrlParam("hash"),
        });
      } else if (urlProperties.value.isTemplate) {
        store.dispatch(ActionTypes.FETCH_TEMPLATE_AND_LABEL, {
          urlProperties: urlProperties.value,
          templateId: getEditorUrlParam("template_id"),
        });
      } else if (urlProperties.value.labelType.length) {
        store.dispatch(ActionTypes.FETCH_EDITOR_PARAMS, {
          pathName,
          urlProperties: urlProperties.value,
        });
      } else {
        urlProperties.value = {
          corners: "",
          isArtwork: false,
          isCustom: false,
          isCreatingTemplate: false,
          isTemplate: false,
          labelId: "SL102",
          labelType: "printed",
          quickLink: "",
        };

        store.dispatch(ActionTypes.FETCH_EDITOR_PARAMS, {
          pathName: "label-design/printed/SL102",
          urlProperties: urlProperties.value,
        });
      }
    });

    const isMobile = computed(() => store.getters.isMobile);
    const currentLabel = computed(() => store.getters.currentLabel);
    const mountingLabel = computed<boolean>(() => store.getters.mountingLabel);
    const rulers = computed<boolean>(() => store.getters.settingsState.rulers);
    const showOnboarding = computed<boolean>(() => store.getters.showOnboarding);

    watch(mountingLabel, (isMounting) => {
      if (!isMounting) {
        switch (urlProperties.value.quickLink) {
          case "troubleShooting": {
            store.dispatch(ActionTypes.SET_MODAL, "troubleShooting");
            break;
          }
          case "smallPage": {
            store.dispatch(ActionTypes.SET_MODAL, "smallPage");
            break;
          }
          case "shiftedVertical": {
            store.dispatch(ActionTypes.SET_MODAL, "shiftedVertical");
            break;
          }
          case "mismatched": {
            store.dispatch(ActionTypes.SET_MODAL, "mismatched");
            break;
          }
          case "alignmentWizard": {
            store.dispatch(ActionTypes.SET_MODAL, "alignmentWizard");
            break;
          }
        }
      }
    });

    /* SIDE TOOL BAR LOCK */
    const lockToolBar = ref(false);
    provide("lockToolBar", lockToolBar);
    const toggleLock = () => {
      lockToolBar.value = !lockToolBar.value;
    };
    /* SIDE TOOL BAR LOCK */

    /* CLIENT SIZE */
    const clientWidth = ref(0);
    const clientHeight = ref(0);

    const clientDimensions = computed<ClientDimensions>(() => store.getters.clientDimensions);

    const timeOut = ref(0);
    const isResizing = ref(false);
    provide("isResizing", isResizing);

    //Updates client dimensions when resizing is finished in Canvas.vue
    const watchClientSize = async () => {
      if (canvasWrap.value) {
        handleCanvasWrapResize(canvasWrap.value);
      }

      isResizing.value = true;
      clearTimeout(timeOut.value);
      timeOut.value = setTimeout(function () {
        isResizing.value = false;
      }, 250);
    };

    onMounted(() => {
      clientWidth.value = document.body.clientWidth;
      clientHeight.value = document.body.clientHeight;

      const payload: ClientDimensions = {
        width: clientWidth.value,
        height: clientHeight.value,
      };

      store.dispatch(ActionTypes.SET_CLIENT_DIMENSIONS, payload);

      window.addEventListener("resize", watchClientSize);
    });
    /* CLIENT SIZE */

    /* COLOR PICKR */
    const colorPickr = computed<ColorPickrState>(() => store.getters.colorPickrState);
    const pickrIsRendered = ref(false);
    const setPickrIsRendered = (value: boolean) => {
      pickrIsRendered.value = value;
    };
    document.addEventListener("click", (ev) => {
      const pickrMain = document.querySelector(".pcr-app") as HTMLDivElement;

      if (
        ev.target &&
        ev.target instanceof Node &&
        pickrIsRendered.value &&
        pickrMain &&
        !pickrMain.contains(ev.target)
      ) {
        store.dispatch(ActionTypes.SET_COLOR_PICKR_STATE, {
          ...colorPickr.value,
          left: pickrMain.style.left,
          top: pickrMain.style.top,
          isOpen: false,
          id: "",
          property: { name: "", value: "" },
        } as ColorPickrState);

        setPickrIsRendered(false);
      }
    });
    /* COLOR PICKR */

    /*
      CANVAS WRAP
    */
    const canvasWrapClientWidth = ref(0);
    const canvasWrapClientHeight = ref(0);

    const handleCanvasWrapResize = (canvasWrapDiv: HTMLDivElement) => {
      canvasWrapClientWidth.value = canvasWrapDiv.clientWidth;
      canvasWrapClientHeight.value = canvasWrapDiv.clientHeight;
    };

    provide("canvasWrapClientWidth", canvasWrapClientWidth);
    provide("canvasWrapClientHeight", canvasWrapClientHeight);

    /*
      CANVAS WRAP
    */

    return {
      isMobile,
      clientDimensions,
      rulers,
      canvasWrap,
      currentLabel,
      colorPickr,
      lockToolBar,
      toggleLock,
      setPickrIsRendered,
      showOnboarding,
    };
  },
});
